import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  toast,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDeletePlotGroup } from '@/api/rest/resources/organization';
import { PlotGroupSuccessResponse } from '@/api/rest/resources/types/organization';

export type DeleteGroupProps = {
  group: PlotGroupSuccessResponse | null;
  onClose: () => void;
  onSuccess?: () => void;
};

export const DeleteGroupWithConfirmation = ({ group, onClose, onSuccess }: DeleteGroupProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [deletingGroup, setDeletingGroup] = useState(false);

  const deleteGroupMutation = useDeletePlotGroup();

  const deleteGroup = async () => {
    if (!group) return;

    try {
      setDeletingGroup(true);
      await deleteGroupMutation.mutateAsync({
        pathVariables: { organization_id: group.organization, plot_group_id: group.id },
      });

      toast({
        type: 'success',
        title: t('global.organization.plotGroup.deleteGroup.toasts.success'),
      });

      if (onSuccess) {
        onSuccess();
      }

      queryClient.resetQueries({ queryKey: ['organization', group.organization, 'plot-groups'] });

      onClose();
    } catch (error) {
      toast({
        type: 'error',
        title: t('global.organization.plotGroup.deleteGroup.toasts.error'),
      });
    } finally {
      setDeletingGroup(false);
    }
  };

  return (
    <Dialog open={!!group} onOpenChange={onClose}>
      <DialogContent data-testid='delete-group-with-confirmation'>
        <DialogHeader className='typography-h2 w-full text-error md:typography-h3 sm:text-center'>
          {t('global.organization.plotGroup.deleteGroup.dialog.title')}
        </DialogHeader>
        <DialogBody className='mb-8'>
          {t('global.organization.plotGroup.deleteGroup.dialog.message', { name: group?.name })}
        </DialogBody>
        <DialogFooter className='justify-end'>
          <DialogClose asChild>
            <Button variant='text'>{t('global.ui.buttons.cancel')}</Button>
          </DialogClose>
          <Button onClick={() => deleteGroup()} loading={deletingGroup} className='bg-error hover:bg-error-dark'>
            {t('global.organization.plotGroup.deleteGroup.dialog.submitButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
